// Pages
import Dashboard from './pages/Dashboard/Dashboard';
import UseCaseDashboard from './pages/UseCases/UseCaseDashboard';
import UseCasesList from './pages/UseCases/UseCasesList';
import UseCaseDetailPage from './pages/UseCases/UseCaseDetailPage'; // Utiliser la nouvelle page dédiée
import NewUseCasePage from './pages/UseCases/NewUseCasePage';
import InterviewsList from './pages/Interviews/InterviewsList';
import QuestionnairesPage from './pages/Questionnaires/QuestionnairesPage';
import QuestionnaireDetailPage from './pages/Questionnaires/QuestionnaireDetailPage';
import QuestionnaireEditPage from './pages/Questionnaires/QuestionnaireEditPage';
import CadrageDetailPage from './pages/Cadrages/CadrageDetailPage'; // Nouvel emplacement et nom
import CadragesListPage from './pages/Cadrages/CadragesListPage'; // Import de la page liste des cadrages
import NewCadragePage from './pages/Cadrages/NewCadragePage'; // Import de la page de création de cadrage
import OrganizationsPage from './pages/Organizations/OrganizationsPage';
import OrganizationDetailsPage from './pages/Organizations/OrganizationDetailsPage';
import NoOrganizationPage from './pages/Organizations/NoOrganizationPage';
import OrganizationDeactivatedPage from './pages/Organizations/OrganizationDeactivatedPage';
import MyOrganizationPage from './pages/Organizations/MyOrganizationPage';
import DomainDetailPage from './pages/Domains/DomainDetailPage';
import DomainsListPage from './pages/Domains/DomainsListPage';
import NewDomainPage from './pages/Domains/NewDomainPage';
import EditDomainPage from './pages/Domains/EditDomainPage';
import TemplatesPage from './pages/Templates/TemplatesPage';
import SettingsPage from './pages/Settings/SettingsPage'; // Import de la page Paramètres
import ProfilePage from './pages/Profile/ProfilePage';
import AIExtractionPage from './pages/AIExtraction/AIExtractionPage'; // Import de la page AI Extraction
import CallbackPage from './pages/Auth/CallbackPage'; // Page de callback Logto
import AcceptInvitePage from './pages/Auth/AcceptInvitePage'; // Page d'acceptation d'invitation
import InvitationsPage from './pages/Organizations/InvitationsPage'; // Page de gestion des invitations
import UsersPage from './pages/Admin/UsersPage'; // Super-admin user management
import TokenConsumptionPage from './pages/Admin/TokenConsumptionPage';
import SuperadminSetupPage from './pages/Admin/SuperadminSetupPage';
import OntologyManagementPage from './pages/OntologyManagement/OntologyManagementPage'; // Phase 06-01: Ontology configuration
import { EvaluationsDashboardPage } from './pages/AIEvaluation/EvaluationsDashboardPage';
import DevelopmentStatusPage from './pages/Status/DevelopmentStatusPage'; // Hidden dev status (URL only: /status)
import ChangelogPage from './pages/Changelog/ChangelogPage';
import { ChatPage } from './pages/Chat';
import { ChatbotsPage } from './pages/Chatbots/ChatbotsPage';
import { ConversationsPage } from './pages/Conversations/ConversationsPage';

// Forge pages
import { ForgesListPage, ForgeDetailPage, ApplicationsListPage, ApplicationDetailPage, ForgeDashboardPage } from './pages/Forge';

// Forms
import NewMissionForm from './pages/Missions/NewMissionForm'; // Gardé pour l'instant, à revoir
import TemplateForm from './pages/Templates/TemplateForm'; // Import du formulaire

// Documentation
import DocumentationDashboard from './pages/Documentation/DocumentationDashboard';
import DocIntroduction from './pages/Documentation/DocIntroduction';
import DocWorkflow from './pages/Documentation/DocWorkflow';
import DocAudits from './pages/Documentation/DocAudits';
import DocUseCases from './pages/Documentation/DocUseCases';
import DocCadrages from './pages/Documentation/DocCadrages';
import DocAIFeatures from './pages/Documentation/DocAIFeatures';
import DocProjects from './pages/Documentation/DocProjects';
import { KnowledgeBasePage } from './pages/KnowledgeBase/KnowledgeBasePage';

// Demo
import { DemoApp } from './pages/Demo/DemoApp';
import { AUTH_MODE, DEMO_ENABLED } from './lib/runtimeFlags';

// Genie Artifact (Phase 2 backbone) — lazy-loaded so Pretext bundle stays out of main chunk
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
const KnowledgeGraphPage = lazy(() => import('./pages/KnowledgeBase/KnowledgeGraphPage'));
const OntologyOwlEditorPage = lazy(() => import('./pages/OntologyManagement/OntologyOwlEditorPage'));
const GenieArtifactPage = lazy(() => import('./pages/GenieArtifact'));
const GenieArtifactSpikePage = lazy(() => import('./pages/GenieArtifactSpike'));
const WelcomePage = lazy(() => import('./pages/Welcome/WelcomePage'));
// CoWork handoff workspace (epic #806) — lazy-loaded, standalone page
const CoWorkPage = lazy(() => import('./pages/CoWork'));
// Projects (lot L1) — lazy-loaded, behind the org's projects / gallery modules
const ProjectsPage = lazy(() => import('./pages/Projects/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('./pages/Projects/ProjectDetailPage'));
const GalleryPage = lazy(() => import('./pages/Projects/GalleryPage'));
// Portfolio (lot L4) — lazy-loaded, behind the portfolio and projects modules
const PortfolioPage = lazy(() => import('./pages/Portfolio/PortfolioPage'));

// Others
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from './components/Layout/AppLayout';
// Genie Menu hidden for now; restore this import and the <GenieMenu /> render below to bring it back.
// import { GenieMenu } from './components/GenieMenu/GenieMenu';
import ProtectedRoute from './components/Auth/ProtectedRoute'; // Import du composant ProtectedRoute
import { AccessCheckPending, GuardedRoute } from './components/Auth/GuardedRoute';
import { useOrgModules } from './hooks/useOrgModules';
import { useLandingPath } from './hooks/useLandingPath';
import { usePermissions } from './hooks/usePermissions';
import ProfilesPage from './pages/Admin/ProfilesPage';
import ProfileEditPage from './pages/Admin/ProfileEditPage';

/**
 * Redirect to the effective user's default welcome page (from their profile),
 * falling back to the first page they can access. Used for the app root and
 * any unmatched path.
 */
function LandingRedirect() {
  const landingPath = useLandingPath();
  // The landing depends on the org's modules (e.g. /projets): wait for them.
  const { loaded } = useOrgModules();
  if (!loaded) return <AccessCheckPending />;
  return <Navigate to={landingPath} replace />;
}

/**
 * A project page opens for project readers, and for gallery readers (who open
 * published projects from the gallery). The backend enforces the rest.
 */
function ProjectDetailGuard() {
  const { canRead } = usePermissions();
  return <GuardedRoute resource={canRead('projects') ? 'projects' : 'gallery'} />;
}

/** Suspense fallback for lazy pages; a component so its text follows the active language. */
function LoadingFallback({ i18nKey = 'common.loading', values }: { i18nKey?: string; values?: Record<string, string> }) {
  const { t } = useTranslation();
  return <div style={{ padding: 24 }}>{t(i18nKey, values)}</div>;
}

function AnalysisPlaceholder() {
  const { t } = useTranslation();
  return <p>{t('common.analysisPagePlaceholder')}</p>;
}

const projectsFallback = <LoadingFallback />;

// Authentication (Logto today; see src/auth/AuthProvider.tsx)
import { AuthProvider } from './auth/AuthProvider';
import { ApiClientProvider } from './contexts/ApiClientProvider';

function App() {
  return (
    <AuthProvider>
      <ApiClientProvider>
      <Router>
        {/* Genie Menu (single FAB for all Genie AI services) — hidden for now */}
        {/* <GenieMenu /> */}

      <Routes>
        {/* Route de callback Logto - DOIT être en dehors des routes protégées */}
        {AUTH_MODE === 'logto' && <Route path="/callback" element={<CallbackPage />} />}
        {AUTH_MODE === 'logto' && <Route path="/auth/callback" element={<CallbackPage />} />}

        {/* Route d'acceptation d'invitation - accessible sans authentification */}
        <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />

        {/* Public demo preparation page — completely outside ProtectedRoute and Logto */}
        {DEMO_ENABLED && <Route path="/demo" element={<DemoApp />} />}

        {/* Routes protégées nécessitant une authentification ET le layout principal */}
        <Route element={<ProtectedRoute />}> {/* Encapsuler les routes protégées */}
          <Route path="/" element={<AppLayout />}>
            <Route index element={<LandingRedirect />} /> {/* Rediriger la racine vers la page d'accueil du profil */}
            {/* Dashboard is gated on domains access (it surfaces the org's domains). */}
            <Route element={<GuardedRoute resource="domains" />}>
              <Route path="dashboard" element={<Dashboard />} />
            </Route>
          {/* Welcome (GenieArtifact) page is gated on genie-artifact access. */}
          <Route element={<GuardedRoute resource="genie-artifact" />}>
            <Route
              path="welcome"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <WelcomePage />
                </Suspense>
              }
            />
          </Route>

          {/* Anciennes routes missions (à nettoyer si plus utilisées) */}
          {/* <Route path="missions" element={<MissionsList />} /> */}
          <Route path="missions/new" element={<NewMissionForm />} /> {/* Gardé pour l'instant, à revoir */}

          {/* Anciennes routes imbriquées /missions/:id supprimées */}
          {/* <Route path="missions/:id" element={<MissionLayout />}> ... </Route> */}

          {/* Études page removed (no audit notion) */}


          {/* Routes spécifiques pour création et détails */}
          <Route element={<GuardedRoute resource="cadrages" />}>
            <Route path="cadrages" element={<CadragesListPage />} /> {/* Liste des cadrages */}
            <Route path="cadrages/new" element={<NewCadragePage />} /> {/* Création cadrage */}
            <Route path="cadrages/:id" element={<CadrageDetailPage />} /> {/* Route détail cadrage */}
          </Route>


          {/* Autres routes */}
          <Route path="profil" element={<ProfilePage />} />
          <Route path="templates" element={<TemplatesPage />} />
          <Route path="templates/new" element={<TemplateForm />} /> {/* Route pour créer */}
          <Route path="templates/edit/:id" element={<TemplateForm />} /> {/* Route pour éditer */}
          <Route path="questionnaires" element={<QuestionnairesPage />} />
          <Route path="questionnaires/:id" element={<QuestionnaireDetailPage />} />
          <Route path="questionnaires/:id/edit" element={<QuestionnaireEditPage />} />
          <Route path="entretiens" element={<InterviewsList />} />
          <Route path="analyse" element={<AnalysisPlaceholder />} />
          <Route path="settings" element={<SettingsPage />} /> {/* Correction: Utilisation de /settings */}
          <Route path="profile" element={<ProfilePage />} /> {/* Correction: Utilisation de /profile */}
          <Route path="organizations" element={<OrganizationsPage />} />
          <Route path="organizations/:id" element={<OrganizationDetailsPage />} />
          <Route path="users" element={<UsersPage />} /> {/* Super-admin user management */}
          <Route path="admin/profiles" element={<ProfilesPage />} /> {/* Super-admin profile management */}
          {/* Tabbed profile editor (plan §9.2); the tab is kept in the URL */}
          <Route path="admin/profiles/new/:tab?" element={<ProfileEditPage />} />
          <Route path="admin/profiles/:profileId/:tab?" element={<ProfileEditPage />} />
          <Route element={<GuardedRoute resource="token-consumption" />}>
            <Route path="token-consumption" element={<TokenConsumptionPage />} />
          </Route>
          {/* Legacy path — keep old bookmarks working */}
          <Route path="admin/token-consumption" element={<Navigate to="/token-consumption" replace />} />
          <Route element={<GuardedRoute resource="configuration" />}>
            <Route path="admin/configuration" element={<OntologyManagementPage />} />
            <Route
              path="admin/configuration/ontology/owl"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <OntologyOwlEditorPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<GuardedRoute resource="evaluations" />}>
            <Route path="evaluations" element={<EvaluationsDashboardPage />} />
          </Route>
          <Route path="superadmin" element={<SuperadminSetupPage />} />
          <Route path="forge-dashboard" element={<ForgeDashboardPage />} /> {/* Forge Dashboard - Admin only */}
          <Route path="no-organization" element={<NoOrganizationPage />} />
          <Route path="organization-deactivated" element={<OrganizationDeactivatedPage />} />
          <Route element={<GuardedRoute resource="my-organization" />}>
            <Route path="my-organization" element={<MyOrganizationPage />} />
          </Route>
          <Route path="invitations" element={<InvitationsPage />} />
          <Route element={<GuardedRoute resource="domains" />}>
            <Route path="domains" element={<DomainsListPage />} />
            <Route path="domains/new" element={<NewDomainPage />} />
            <Route path="domains/:domainId/edit" element={<EditDomainPage />} />
            <Route path="domains/:domainId" element={<DomainDetailPage />} />
          </Route>
          <Route element={<GuardedRoute resource="usecases" />}>
            <Route path="cas-usage" element={<UseCaseDashboard />} />
            <Route path="cas-usage/list" element={<UseCasesList />} />
            <Route path="cas-usage/new" element={<NewUseCasePage />} />
            <Route path="cas-usage/:id" element={<UseCaseDetailPage />} />
          </Route>

          {/* Projects (lot L1): « Mes projets », project page (tab in the URL), gallery */}
          <Route element={<GuardedRoute resource="projects" />}>
            <Route
              path="projets"
              element={
                <Suspense fallback={projectsFallback}>
                  <ProjectsPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<ProjectDetailGuard />}>
            <Route
              path="projets/:id/:tab?"
              element={
                <Suspense fallback={projectsFallback}>
                  <ProjectDetailPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<GuardedRoute resource="gallery" />}>
            <Route
              path="galerie"
              element={
                <Suspense fallback={projectsFallback}>
                  <GalleryPage />
                </Suspense>
              }
            />
          </Route>
          {/* Portfolio (lot L4); the page also requires the projects module */}
          <Route element={<GuardedRoute resource="portfolio" />}>
            <Route
              path="portefeuille"
              element={
                <Suspense fallback={projectsFallback}>
                  <PortfolioPage />
                </Suspense>
              }
            />
          </Route>

          <Route path="ai-extraction" element={<AIExtractionPage />} /> {/* Route pour AI Document Extraction */}
          <Route element={<GuardedRoute resource="knowledge-base" />}>
            <Route path="knowledge-base" element={<KnowledgeBasePage />} />
            <Route
              path="knowledge-base/graph"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <KnowledgeGraphPage />
                </Suspense>
              }
            />
          </Route>
          <Route path="chat" element={<ChatPage />} /> {/* Route pour Assistant IA (Phase 2) */}
          <Route element={<GuardedRoute resource="genie-artifact" />}>
            <Route
              path="genie-artifact"
              element={
                <Suspense fallback={<LoadingFallback i18nKey="common.loadingNamed" values={{ name: 'Genie Artifact' }} />}>
                  <GenieArtifactPage />
                </Suspense>
              }
            />
            <Route
              path="genie-artifact/:sessionId"
              element={
                <Suspense fallback={<LoadingFallback i18nKey="common.loadingNamed" values={{ name: 'Genie Artifact' }} />}>
                  <GenieArtifactPage />
                </Suspense>
              }
            />
            <Route
              path="genie-artifact-spike"
              element={
                <Suspense fallback={<LoadingFallback i18nKey="common.loadingPretextCanvas" />}>
                  <GenieArtifactSpikePage />
                </Suspense>
              }
            />
          </Route>

          {/* CoWork handoff workspace (epic #806) */}
          <Route element={<GuardedRoute resource="cowork" />}>
            <Route
              path="cowork"
              element={
                <Suspense fallback={<LoadingFallback i18nKey="common.loadingNamed" values={{ name: 'CoWork' }} />}>
                  <CoWorkPage />
                </Suspense>
              }
            />
          </Route>

          <Route element={<GuardedRoute resource="conversations" />}>
            <Route path="conversations" element={<ConversationsPage />} />
          </Route>
          <Route element={<GuardedRoute resource="chatbots" />}>
            <Route path="chatbots" element={<ChatbotsPage />} />
          </Route>

          {/* Documentation routes */}
          <Route path="documentation" element={<DocumentationDashboard />} />
          <Route path="documentation/introduction" element={<DocIntroduction />} />
          <Route path="documentation/workflow" element={<DocWorkflow />} />
          <Route path="documentation/audits" element={<DocAudits />} />
          <Route path="documentation/use-cases" element={<DocUseCases />} />
          <Route path="documentation/cadrages" element={<DocCadrages />} />
          <Route path="documentation/projects" element={<DocProjects />} />
          <Route path="documentation/ai-features" element={<DocAIFeatures />} />

          {/* Forge routes */}
          <Route element={<GuardedRoute resource="forges" />}>
            <Route path="forges" element={<ForgesListPage />} />
            <Route path="forges/:forgeId" element={<ForgeDetailPage />} />
          </Route>
          <Route element={<GuardedRoute resource="applications" />}>
            <Route path="applications" element={<ApplicationsListPage />} />
            <Route path="applications/:appId" element={<ApplicationDetailPage />} />
          </Route>

          <Route path="status" element={<DevelopmentStatusPage />} /> {/* Development Status (hidden from nav, URL only) */}
          <Route path="changelog" element={<ChangelogPage />} /> {/* What's New / Changelog */}

          {/* La redirection par défaut est maintenant gérée par la route index */}
          {/* <Route path="*" element={<Navigate to="/welcome" replace />} /> */}
            {/* Note: Une gestion plus fine des 404 pourrait être ajoutée ici */}
          </Route>
        </Route>

        {/* Fallback général si aucune route ne correspond (y compris hors layout) */}
        {/* Redirige vers la page d'accueil du profil ; ProtectedRoute interceptera si non authentifié */}
        {/* Unknown paths go through the index route, inside the layout that loads the org settings */}
        <Route path="*" element={<Navigate to="/" replace />} />
        {/* Ou rediriger vers /login si non authentifié, ou une page 404 */}
      </Routes>
    </Router>
      </ApiClientProvider>
    </AuthProvider>
  );
}

export default App;
